import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'

import { PresentationChartLineIcon } from '@gto/icons-react/outline/presentation-chart-line'
import type { ColumnDef } from '@gto/ui/data-table'
import { createColumns } from '@gto/ui/data-table'
import { Typography } from '@gto/ui/typography'

import type { AffiliateAnalyticsRowSchema } from '@/api/gen'
import { useGetPartnerAnalyticsView } from '@/api/gen'
import { DataTable } from '@/components/data-table'
import { EmptyState } from '@/components/empty-state'
import { InlineQueryError } from '@/components/inline-query-error'
import { StatsCard } from '@/components/stats-card'
import { fluidColumn } from '@/components/table-columns'
import { useReportQueryError } from '@/hooks/use-report-query-error'
import { useGranularitySnapshot } from '@/utils/granularity'
import { toAmount } from '@/utils/to-amount'

const useReportColumns = (): ColumnDef<AffiliateAnalyticsRowSchema>[] => {
  const { formatMessage } = useIntl()

  return createColumns<AffiliateAnalyticsRowSchema>([
    {
      accessorKey: 'period_label',
      header: formatMessage({ id: 'admin.partnerDetail.report.month' }),
      ...fluidColumn({ min: 120, flex: 1 }),
      enableSorting: false,
    },
    {
      accessorKey: 'revenue_usd',
      header: formatMessage({ id: 'admin.partnerDetail.report.revenue' }),
      ...fluidColumn({ min: 120, flex: 1, align: 'right' }),
      enableSorting: false,
      cell: ({ row }) => (
        <FormattedNumber
          currency="USD"
          maximumFractionDigits={0}
          style="currency"
          value={toAmount(row.original.revenue_usd, 'partner-detail report revenue_usd:')}
        />
      ),
    },
    {
      accessorKey: 'commission_usd',
      header: formatMessage({ id: 'admin.partnerDetail.report.commissions' }),
      ...fluidColumn({ min: 120, flex: 1, align: 'right' }),
      enableSorting: false,
      cell: ({ row }) => (
        <Typography
          color="primary"
          variant="body2"
        >
          <FormattedNumber
            currency="USD"
            maximumFractionDigits={0}
            style="currency"
            value={toAmount(row.original.commission_usd, 'partner-detail report commission_usd:')}
          />
        </Typography>
      ),
    },
  ])
}

const getReportRowId = (row: AffiliateAnalyticsRowSchema) => row.period_start

export const ReportTab = ({ partnerId }: { partnerId: string }) => {
  // Reuse the monthly range that powers the Overview chart — months, no toggle.
  // Snapshot in state so `getDateRange()`'s `new Date()` runs once on mount,
  // not every render (the granularity is constant; only the wall clock would
  // otherwise drift the query key across renders).
  const { dateRange } = useGranularitySnapshot('monthly')
  const analyticsQuery = useGetPartnerAnalyticsView(partnerId, {
    ...dateRange,
    granularity: 'monthly',
  })
  const { data: analytics, isLoading, isError, refetch } = analyticsQuery

  // Forward load failures to Sentry — the inline error UI below already covers the
  // user-facing surface; this only adds the missing observability hop. Reports
  // only on TERMINAL failure so retries don't multiply Sentry events.
  useReportQueryError(analyticsQuery, 'getPartnerAnalyticsView failed: ')

  const columns = useReportColumns()
  // Rows arrive newest-first from the API; most-recent-month-first reads best
  // for a report table, so keep that order (the cache array is never mutated).
  const rows = analytics?.rows ?? []

  if (isError) {
    return (
      <InlineQueryError
        data-tst="report-error"
        messageId="admin.partnerDetail.report.error"
        onRetry={() => void refetch()}
      />
    )
  }

  if (!isLoading && rows.length === 0) {
    return <EmptyState title={<FormattedMessage id="admin.partnerDetail.report.empty" />} />
  }

  return (
    <StatsCard
      icon={<PresentationChartLineIcon />}
      title={<FormattedMessage id="admin.partnerDetail.report.title" />}
    >
      <DataTable
        columns={columns}
        data={rows}
        getRowId={getReportRowId}
        loading={isLoading}
      />
    </StatsCard>
  )
}
